import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import RefreshIcon from '@mui/icons-material/Refresh'
import { Alert, Box, Button, Container, Typography } from '@mui/material'
import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * ErrorBoundary - Catches React component errors and displays a fallback UI
 *
 * Wraps components to prevent the entire app from crashing when an error occurs.
 * Provides a user-friendly error message with a retry option.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    this.setState({
      error,
      errorInfo
    })
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    })
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI if provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <Container maxWidth='sm' sx={{ mt: 8 }}>
          <Box sx={{ textAlign: 'center' }}>
            <ErrorOutlineIcon
              sx={{ fontSize: 64, color: 'error.main', mb: 2 }}
            />
            <Typography variant='h5' gutterBottom sx={{ fontWeight: 600 }}>
              Something went wrong
            </Typography>
            <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
              We encountered an unexpected error. Please try refreshing the
              page.
            </Typography>

            <Alert severity='error' sx={{ mb: 3, textAlign: 'left' }}>
              <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
                Error Details:
              </Typography>
              <Typography
                variant='caption'
                component='pre'
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {this.state.error?.message || 'Unknown error'}
              </Typography>
            </Alert>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant='contained'
                startIcon={<RefreshIcon />}
                onClick={() => window.location.reload()}
              >
                Refresh Page
              </Button>
              <Button variant='outlined' onClick={this.handleReset}>
                Try Again
              </Button>
            </Box>
          </Box>
        </Container>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
